import { Link } from 'react-router-dom'
import { useI18n } from '../i18n'
import { ageFromDob } from '../lib/format'

export interface CardHorse {
  id: string
  name: string
  profilePhotoUrl?: string
  breed?: string
  gender?: string
  discipline?: string
  colour?: string
  country?: string
  dob?: string
  status?: string
  public?: boolean
  followerCount?: number
  /** Set when this horse belongs to someone else and is shared with the viewer. */
  sharedRole?: string
  ownerName?: string
}

const statusStyles: Record<string, string> = {
  active: 'bg-emerald-500/90 text-white',
  sold: 'bg-blue-500/90 text-white',
  retired: 'bg-amber-500/90 text-white',
  deceased: 'bg-gray-500/90 text-white',
}

export function HorseCard({ horse, to, showStatus }: { horse: CardHorse; to: string; showStatus?: boolean }) {
  const { t, td } = useI18n()
  const age = ageFromDob(horse.dob)
  return (
    <Link to={to} className="card group overflow-hidden transition hover:shadow-lift">
      <div className="relative aspect-[4/3] overflow-hidden bg-brand-100 dark:bg-brand-800">
        {horse.profilePhotoUrl ? (
          <img src={horse.profilePhotoUrl} alt={horse.name} loading="lazy" className="h-full w-full object-cover transition duration-500 group-hover:scale-105" />
        ) : (
          <div className="grid h-full w-full place-items-center text-5xl">🐎</div>
        )}
        <div className="absolute left-3 top-3 flex gap-2">
          {showStatus && horse.status && (
            <span className={`rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${statusStyles[horse.status] || 'bg-black/60 text-white'}`}>
              {td('status', horse.status)}
            </span>
          )}
          {showStatus && horse.public === false && (
            <span className="rounded-full bg-black/60 px-2.5 py-1 text-xs font-semibold text-white backdrop-blur">{t('horse.private')}</span>
          )}
        </div>
      </div>
      <div className="p-5">
        <h3 className="text-lg font-bold">{horse.name}</h3>
        {horse.sharedRole && (
          <p className="mt-0.5 text-xs font-medium text-brand-500 dark:text-brand-400">
            {horse.sharedRole === 'viewer' ? '👁' : '🤝'} {t(
              horse.sharedRole === 'viewer' ? 'horse.access.badgeViewer' : 'horse.access.badgeCoOwner',
              { owner: horse.ownerName || '' },
            )}
          </p>
        )}
        <p className="text-sm text-brand-600 dark:text-brand-300">
          {[horse.breed, horse.gender && td('gender', horse.gender), age != null && `${age} ${t('units.yrs')}`].filter(Boolean).join(' · ')}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {horse.discipline && <span className="chip">{td('discipline', horse.discipline)}</span>}
          {horse.colour && <span className="chip">{td('colour', horse.colour)}</span>}
          {horse.country && <span className="chip">📍 {horse.country}</span>}
          {horse.followerCount != null && horse.followerCount > 0 && <span className="chip">❤ {horse.followerCount}</span>}
        </div>
      </div>
    </Link>
  )
}
